import React from 'react'
import styled from 'styled-components'
import { InspectorData, TextOrLayerStyle } from 'modules/shares/Inspector/types'
import Section from '../Section'
import Header, { Title } from '../Header'
import Separator from '../Separator'
import { TruncateWithTooltip } from 'components/Truncate'
import { formatPath } from 'modules/shares/Inspector/Sidebar/utils'
import { Layer } from 'modules/shares/types'

interface TextOrLayerStylesProps {
  styles: InspectorData['textStyles'] | InspectorData['layerStyles']
  layer: Layer
  title: string
}

const getNameAndPath = (
  style: TextOrLayerStyle
): { name: string; path: string } => {
  const textStylePath = style.name.split('/')
  const name = textStylePath.pop()

  const documentName = style.isForeign ? style.documentName : 'This Document'

  return {
    name: name?.trim() ?? '',
    path: formatPath([documentName || '', ...textStylePath]),
  }
}

const TextOrLayerStyles = ({
  styles,
  title,
  layer,
}: TextOrLayerStylesProps) => {
  if (!styles[layer.sharedStyleID]) {
    return null
  }

  const style = styles[layer.sharedStyleID]
  const { name, path } = getNameAndPath(style)
  const sectionCopyValue = style.name

  const isDirty = !!style.value.isDirty || !!layer.style?.isDirty

  return (
    <>
      <Separator />
      <Section data-testid="inspector-sidebar-styles">
        <Header copyValue={sectionCopyValue}>
          <Title>{title}</Title>
        </Header>
        <Container>
          <Name isDirty={isDirty}>
            {name}
            {isDirty && `*`}
          </Name>
          <Path>{path}</Path>
        </Container>
      </Section>
    </>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background.light.base};
  border: 1px solid ${({ theme }) => theme.colors.border.dark.extraHigh};
  border-radius: ${({ theme }) => theme.radii.base};
  color: ${({ theme }) => theme.colors.text.dark.primary};
  padding: 8px 12px;
  margin-right: 24px;
`

const Name = styled.span<{ isDirty: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.smaller};
  color: ${({ theme }) => theme.colors.text.dark.secondary};
  font-style: ${props => (props.isDirty ? 'italic' : 'normal')};
`

const Path = styled(TruncateWithTooltip)`
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
  font-size: ${({ theme }) => theme.fontSizes.tiny};
`

export default TextOrLayerStyles
